/**
 * Synti Session Viewer
 *
 * A minimal web app for viewing Synti session transcripts.
 * Users can upload session JSON files or view shared sessions via URL.
 *
 * Routes:
 * - / - Upload interface
 * - /s/{id} - View shared session
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Lock } from 'lucide-react'
import type {
  StoredSession,
  EncryptedShareEnvelope,
  ShareMode,
  SharedFile,
  AirSharePresentation,
} from '@synti/core'
import { isEncryptedShare, decryptShare, ShareDecryptError } from '@synti/core'
import {
  SessionViewer,
  GenericOverlay,
  CodePreviewOverlay,
  MultiDiffPreviewOverlay,
  TerminalPreviewOverlay,
  JSONPreviewOverlay,
  DocumentFormattedMarkdownOverlay,
  TooltipProvider,
  extractOverlayData,
  detectLanguage,
  openExternalUrl,
  type PlatformActions,
  type ActivityItem,
  type OverlayData,
  type FileChange,
} from '@synti/ui'
import { SessionUpload } from './components/SessionUpload'
import { Header } from './components/Header'
import { AirShareHeader } from './components/AirShareHeader'
import { AirNodeView } from './components/AirNodeView'
import { AirCanvasView } from './components/AirCanvasView'

/**
 * The assistant's turns from a shared session, in order.
 *
 * Only for cards whose own text is missing — see `AirCanvasView`'s `answers`.
 * Plain string content only: anything richer was already rendered by whatever
 * put it on the card, and guessing at a tool-call transcript is how a fallback
 * starts inventing.
 */
function answersFromSession(session: StoredSession | null): string[] {
  if (!session?.messages) return []
  return session.messages
    .filter((m) => (m as { type?: string }).type === 'assistant')
    .map((m) => {
      const c = (m as { content?: unknown }).content
      return typeof c === 'string' ? c : ''
    })
}

/** Default session ID for development */
const DEV_SESSION_ID = 'tz5-13I84pwK_he'

/** Extract session ID from URL path /s/{id} */
function getSessionIdFromUrl(): string | null {
  const path = window.location.pathname
  const match = path.match(/^\/s\/([a-zA-Z0-9_-]+)$/)
  if (match) return match[1]

  // In development, redirect root to default session
  if (import.meta.env.DEV && path === '/') {
    window.history.replaceState({}, '', `/s/${DEV_SESSION_ID}`)
    return DEV_SESSION_ID
  }

  return null
}

export function App() {
  const { t } = useTranslation()
  const [session, setSession] = useState<StoredSession | null>(null)
  const [shareMode, setShareMode] = useState<ShareMode>('full')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(() => getSessionIdFromUrl())
  // PIN-protected shares: the encrypted envelope awaiting a PIN, plus input state.
  const [pinEnvelope, setPinEnvelope] = useState<EncryptedShareEnvelope | null>(null)
  const [pinValue, setPinValue] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)
  /**
   * The files the share carried, keyed by the path the transcript names them by.
   *
   * A transcript points at pictures and artefacts by absolute path, which is a
   * fact about the machine that made them. Here there is no such machine, so
   * this map is the whole of the filesystem the page has.
   */
  const [files, setFiles] = useState<Record<string, SharedFile>>({})
  /**
   * Set when the share came from the canvas rather than from a chat. Absent for
   * every chat share, which is what keeps the chat viewer unchanged.
   */
  const [air, setAir] = useState<AirSharePresentation | null>(null)
  // For a canvas share, which way it is being read. Canvas first — the spatial
  // arrangement is the thing a canvas share adds — with the transcript one tap
  // away. Reset per share, below.
  const [airView, setAirView] = useState<'canvas' | 'transcript'>('canvas')
  const [isDark, setIsDark] = useState(() => {
    // Check system preference on mount
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  // Fetch session from API when we have a session ID
  useEffect(() => {
    if (!sessionId) return

    const fetchSession = async () => {
      setIsLoading(true)
      setError(null)
      setSession(null)
      setPinEnvelope(null)
      setPinError(null)
      setFiles({})
      setAir(null)

      try {
        const response = await fetch(`/s/api/${sessionId}`)
        if (!response.ok) {
          if (response.status === 404) {
            setError(t('errors.sessionNotFound'))
          } else {
            setError(t('errors.failedToLoadSession'))
          }
          return
        }

        const data = await response.json()
        if (isEncryptedShare(data)) {
          // PIN-protected: gate on the PIN prompt; nothing is revealed yet.
          setPinEnvelope(data.envelope)
        } else if (data && typeof data === 'object' && data.v === 1) {
          // SharePayloadV1 (subset / single / full).
          setSession(data.session)
          setShareMode(data.mode ?? 'full')
          setFiles(data.files ?? {})
          setAir(data.air ?? null)
        } else {
          // Legacy: a bare StoredSession (pre-`v` shares).
          setSession(data)
          setShareMode('full')
        }
      } catch (err) {
        console.error('Failed to fetch session:', err)
        setError(t('errors.failedToLoadSession'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchSession()
  }, [sessionId])

  // Handle browser navigation
  useEffect(() => {
    const handlePopState = () => {
      const newId = getSessionIdFromUrl()
      setSessionId(newId)
      if (!newId) {
        setSession(null)
        setError(null)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Apply dark mode class to html element
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark)
  }, [isDark])

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches)
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  const handleSessionLoad = useCallback((loadedSession: StoredSession) => {
    setSession(loadedSession)
  }, [])

  // Unlock a PIN-protected share: decrypt the envelope with the entered PIN.
  const handlePinSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!pinEnvelope || !pinValue) return
      setPinError(null)
      try {
        const payload = await decryptShare(pinEnvelope, pinValue)
        setSession(payload.session)
        setShareMode(payload.mode ?? 'full')
        setFiles(payload.files ?? {})
        setAir(payload.air ?? null)
        setPinEnvelope(null)
        setPinValue('')
      } catch (err) {
        setPinError(
          err instanceof ShareDecryptError
            ? t('errors.incorrectPin')
            : t('errors.failedToLoadSession'),
        )
      }
    },
    [pinEnvelope, pinValue, t],
  )

  const handleClear = useCallback(() => {
    setSession(null)
    setSessionId(null)
    setError(null)
    // Update URL to root
    window.history.pushState({}, '', '/')
  }, [])

  const toggleTheme = useCallback(() => {
    setIsDark(prev => !prev)
  }, [])

  // State for overlay
  const [overlayActivity, setOverlayActivity] = useState<ActivityItem | null>(null)
  // State for multi-diff overlay (Edit/Write activities shown as diffs)
  const [multiDiffState, setMultiDiffState] = useState<{ changes: FileChange[] } | null>(null)

  // Handle activity click - Edit/Write opens multi-diff, others use extractOverlayData
  const handleActivityClick = useCallback((activity: ActivityItem) => {
    if (activity.toolName === 'Edit' || activity.toolName === 'Write') {
      const input = activity.toolInput as Record<string, unknown> | undefined
      // Claude fields are primary; PI fields are additive fallbacks.
      const filePath = (input?.file_path as string) || (input?.path as string) || 'unknown'
      const change: FileChange = {
        id: activity.id,
        filePath,
        toolType: activity.toolName,
        original: activity.toolName === 'Edit'
          ? ((input?.old_string as string) || (input?.oldText as string) || '')
          : '',
        modified: activity.toolName === 'Edit'
          ? ((input?.new_string as string) || (input?.newText as string) || '')
          : ((input?.content as string) || ''),
        error: activity.error || undefined,
      }
      setMultiDiffState({ changes: [change] })
    } else {
      setOverlayActivity(activity)
    }
  }, [])

  const handleCloseOverlay = useCallback(() => {
    setOverlayActivity(null)
    setMultiDiffState(null)
  }, [])

  // Extract overlay data using shared parser (non-Edit/Write tools only)
  const overlayData: OverlayData | null = useMemo(() => {
    if (!overlayActivity) return null
    return extractOverlayData(overlayActivity)
  }, [overlayActivity])

  // Platform actions for the viewer (limited functionality)
  //
  // Reading a file is the one place the viewer differs from the app in kind
  // rather than in degree: the app reads from disk, and here the share *is* the
  // disk. Anything the share did not carry is missing, and saying so is better
  // than a picture frame that never fills.
  const platformActions: PlatformActions = useMemo(
    () => ({
      onOpenUrl: (url: string) => {
        const result = openExternalUrl(url)
        if (!result.opened) {
          const detail = result.reason === 'dangerous' ? result.detail : result.reason
          console.warn('[viewer:onOpenUrl] blocked URL:', detail, url)
        }
      },
      onCopyToClipboard: async (text: string) => {
        await navigator.clipboard.writeText(text)
      },
      // An artefact that is a React app is not an artefact until it runs, and
      // here it can be allowed to: the document is handed to the frame as
      // `srcdoc` with `allow-scripts` and *without* `allow-same-origin`, so it
      // executes in an origin of its own — no cookies of ours, no storage of
      // ours, no reach into this page. That is what makes a second domain
      // unnecessary; the origin it runs in is local to the frame.
      //
      // The app does the opposite for the same reason: there the frame shares
      // the app's origin so a file path resolves, and scripts must stay off.
      htmlArtefactsMayRunScripts: true,
      onReadFile: async (path: string) => {
        const file = files[path]
        if (!file) throw new Error(t('errors.fileNotShared'))
        // The HTML of an artefact — decoded straight from base64, and rendered
        // by the same sandboxed frame the app uses, which runs no scripts.
        return new TextDecoder().decode(
          Uint8Array.from(atob(file.data), (c) => c.charCodeAt(0)),
        )
      },
      onReadFileDataUrl: async (path: string) => {
        const file = files[path]
        if (!file) throw new Error(t('errors.fileNotShared'))
        return `data:${file.mime};base64,${file.data}`
      },
    }),
    [files, t],
  )

  const theme = isDark ? 'dark' : 'light'

  return (
    <TooltipProvider>
    <div className="h-full flex flex-col bg-foreground-2 text-foreground">
      <Header
        hasSession={!!session}
        showClear={!sessionId}
        sessionTitle={session?.name}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        onClear={handleClear}
      />

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center text-muted-foreground">
            <div className="animate-pulse">Loading session...</div>
          </div>
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <div className="text-destructive mb-4">{error}</div>
            <button
              onClick={handleClear}
              className="px-4 py-2 rounded-md bg-background text-foreground shadow-sm border border-border hover:bg-foreground/5 transition-colors"
            >
              Go back
            </button>
          </div>
        </div>
      ) : pinEnvelope ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <form onSubmit={handlePinSubmit} className="w-full max-w-xs text-center">
            <Lock className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
            <div className="mb-1 font-medium text-foreground">{t('viewer.pinRequiredTitle')}</div>
            <div className="mb-4 text-sm text-muted-foreground">{t('viewer.pinRequiredHint')}</div>
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              value={pinValue}
              onChange={(e) => { setPinValue(e.target.value); setPinError(null) }}
              placeholder={t('viewer.pinPlaceholder')}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-center text-foreground shadow-sm outline-none focus:border-foreground/30"
            />
            {pinError && <div className="mt-2 text-sm text-destructive">{pinError}</div>}
            <button
              type="submit"
              disabled={!pinValue}
              className="mt-4 w-full rounded-md bg-foreground px-4 py-2 text-background shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {t('viewer.pinUnlock')}
            </button>
          </form>
        </div>
      ) : session ? (
        // A canvas share says what it is above the conversation; a chat share
        // renders exactly as it always has.
        <div className="flex min-h-0 flex-1 flex-col">
          {air && <AirShareHeader air={air} files={files} />}
          {/* A canvas share can be read two ways: as the canvas it was, or as the
              transcript it flattens to. The toggle appears only when the share
              actually carried a canvas — an older one has nothing to switch to. */}
          {air?.canvas?.nodes?.length ? (
            <div className="flex items-center justify-center gap-0.5 border-b border-border/60 py-1.5">
              <div className="flex items-center gap-0.5 rounded-lg bg-foreground/[0.05] p-0.5">
                {(['canvas', 'transcript'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAirView(v)}
                    className={
                      'rounded-md px-3 py-1 text-[12.5px] font-medium transition-colors ' +
                      (airView === v
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground')
                    }
                  >
                    {v === 'canvas'
                      ? t('viewer.canvasView', { defaultValue: 'Canvas' })
                      : t('viewer.transcriptView', { defaultValue: 'Transcript' })}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {air?.canvas?.nodes?.length && airView === 'canvas' ? (
            <AirCanvasView canvas={air.canvas} files={files} answers={answersFromSession(session)} />
          ) : air?.kind === 'node' ? (
            // One exchange from a canvas is a card, not correspondence.
            <AirNodeView session={session} />
          ) : (
          <SessionViewer
            session={session}
            mode="readonly"
            platformActions={platformActions}
            defaultExpanded={shareMode === 'single'}
            className="flex-1 min-h-0"
            onActivityClick={handleActivityClick}
            brandingHref="https://aura.synti.co"
          />
          )}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center p-8">
          <SessionUpload onSessionLoad={handleSessionLoad} />
        </div>
      )}

      {/* Code preview overlay for Read/Write tools */}
      {overlayData?.type === 'code' && (
        <CodePreviewOverlay
          isOpen={!!overlayActivity}
          onClose={handleCloseOverlay}
          content={overlayData.content}
          filePath={overlayData.filePath}
          mode={overlayData.mode}
          startLine={overlayData.startLine}
          totalLines={overlayData.totalLines}
          numLines={overlayData.numLines}
          theme={theme}
          error={overlayData.error}
          command={overlayData.command}
        />
      )}

      {/* Multi-diff preview overlay for Edit/Write tools */}
      {multiDiffState && (
        <MultiDiffPreviewOverlay
          isOpen={true}
          onClose={handleCloseOverlay}
          changes={multiDiffState.changes}
          consolidated={false}
          theme={theme}
        />
      )}

      {/* Terminal preview overlay for Bash/Grep/Glob tools */}
      {overlayData?.type === 'terminal' && (
        <TerminalPreviewOverlay
          isOpen={!!overlayActivity}
          onClose={handleCloseOverlay}
          command={overlayData.command}
          output={overlayData.output}
          exitCode={overlayData.exitCode}
          toolType={overlayData.toolType}
          description={overlayData.description}
          theme={theme}
        />
      )}

      {/* JSON preview overlay for tools returning JSON data */}
      {overlayData?.type === 'json' && (
        <JSONPreviewOverlay
          isOpen={!!overlayActivity}
          onClose={handleCloseOverlay}
          data={overlayData.data}
          title={overlayData.title}
          theme={theme}
          error={overlayData.error}
        />
      )}

      {/* Document overlay for formatted markdown content (Write tool on .md/.txt, WebSearch results) */}
      {overlayData?.type === 'document' && (
        <DocumentFormattedMarkdownOverlay
          isOpen={!!overlayActivity}
          onClose={handleCloseOverlay}
          content={overlayData.content}
          filePath={overlayData.filePath}
          typeBadge={{ icon: FileText, label: overlayData.toolName, variant: 'default' }}
          onOpenUrl={platformActions.onOpenUrl}
          error={overlayData.error}
        />
      )}

      {/* Generic overlay for unknown tools - route markdown to fullscreen viewer */}
      {overlayData?.type === 'generic' && (
        detectLanguage(overlayData.content) === 'markdown' ? (
          <DocumentFormattedMarkdownOverlay
            isOpen={!!overlayActivity}
            onClose={handleCloseOverlay}
            content={overlayData.content}
            onOpenUrl={platformActions.onOpenUrl}
            error={overlayData.error}
          />
        ) : (
          <GenericOverlay
            isOpen={!!overlayActivity}
            onClose={handleCloseOverlay}
            content={overlayData.content}
            title={overlayData.title}
            theme={theme}
          />
        )
      )}
    </div>
    </TooltipProvider>
  )
}
